import { useState, useEffect, useMemo, useCallback, startTransition, MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PointOfSaleIcon,
  MarketsIcon,
  PersonAddIcon as CustomersIcon,
  ProductIcon,
  StoreIcon,
  SettingsIcon,
  OrderIcon,
  DataPresentationIcon as ReportIcon,
  HomeIcon,
  MegaphoneFilledIcon,
  ChatIcon,
  StarIcon,
  PhoneIcon,
  ContentIcon,
  MegaphoneIcon,
  DiscountIcon,
  DeliveryIcon,
  AppsIcon,
  HomeFilledIcon,
  OrderFilledIcon,
  ProductFilledIcon,
  ChartVerticalFilledIcon as ReportFilledIcon,
  StoreFilledIcon,
  SettingsFilledIcon,
  PersonFilledIcon as CustomersFilledIcon,
  CatalogIcon, CashDollarIcon, InventoryIcon, PackageIcon, ReceiptIcon, GaugeIcon, AutomationIcon,
} from '@/lib/polaris-icons';
import { Navigation, Box, SkeletonBodyText, type IconSource } from '@/lib/polaris';
import styles from './Sidebar.module.css';
import { CabeceraDelMenuMovil } from './CabeceraDelMenuMovil';
import { useAuth } from '@smarty/shared';
import { esLaPlataforma } from '@/lib/dropshipping/quienCobra';
import { ganaElMasEspecifico } from '@/lib/navegacion/elMasEspecificoGana';
import { useDatosCacheados } from '@/hooks/useDatosCacheados';
import { proveedorDelTenant } from '@/lib/products/smartyDropApi';
import { useFeatureFlags } from '@smarty/shared';
import { FeatureName } from '@/config/features.config';
import { ROUTES, storePath } from '@/config/routes.config';
import { useOpsActiveSection } from '@/store/useOpsActiveSection';
import { SIDEBAR_SUBNAV, NAV_BADGES, SIDEBAR_MAIN, MENU_DEL_ROL_DROP, type SidebarNavItem } from '@/config/navigation.config';
import { supabase } from '@/lib/supabase';
import { logError } from '@/lib/logger';
import { prefetchSection, prefetchLowPriorityAdminChunks } from '@/lib/prefetchRouteChunks';
import { useQueryClient } from '@tanstack/react-query';
import { prefetchDashboardStats } from '@/lib/dashboardPrefetch';
import {
  prefetchCodAnalyticsStats,
  prefetchLoyaltyDashboardStats,
  prefetchStatisticsStats,
  prefetchCustomersStats,
  prefetchProductsStats,
  prefetchExistenciasStats,
  prefetchEcommerceOrdersStats,
  prefetchSalesOrdersStats,
  prefetchAbandonedCartsStats,
  prefetchShalomAgenciesStats,
  prefetchCategoriesStats,
  prefetchModifiersStats,
  prefetchTagsStats,
  prefetchVariantsStats,
  prefetchEcommercePaymentMethodsStats,
  prefetchEcommerceDeliveryMethodsStats,
  prefetchEcommerceDeliveryRulesStats,
  prefetchEcommercePagesStats,
  prefetchTenantDomainsStats,
  prefetchLogisticsCouriersCoverageStats,
} from '@/lib/prefetchStatsPages';
import { prefetchOnMainNavHover, scheduleIdleProductListPrefetch } from '@/lib/prefetchNavHover';
import { marcarHito } from '@/lib/analytics/hitosDelArranque';
import { useWaPermissions } from '@/hooks/useWaPermissions';
import { useOpsRole } from '@/hooks/useOpsRole';
import { opsVisibleQueues, type OpsQueue } from '@/lib/operations/orderMath';
import { CLAVES_DROP } from '@/lib/dropshipping/clavesDeCache';
import { misPapeles } from '@/lib/dropshipping/actoresDelDrop';
import { useAdministraLaPlata } from '@/hooks/useAdministraLaPlata';
import { laRutaEsDelRolCourier } from '@/lib/dropshipping/rutasDelRolCourier';
import { laRutaEsDelRolAlmacen, MENU_DEL_ROL_ALMACEN_URLS } from '@/lib/dropshipping/rutasDelRolAlmacen';
import { laRutaEsDelSoloAlmacen } from '@/lib/dropshipping/rutasDelSoloAlmacen';
import { useSoloAlmacen } from '@/hooks/useSoloAlmacen';
import { laRutaEsDelVendedorDrop, MENU_DEL_VENDEDOR_DROP, URLS_DEL_VENDEDOR_DROP } from '@/lib/dropshipping/rutasDelVendedorDrop';
import { useSoyVendedorDelDrop } from '@/hooks/useSoyVendedorDelDrop';
import { misMarketplacesIntegrados } from '@/lib/marketplaces/integrados';

interface SubNavItem {
  url: string;
  label: string;
  badge?: string;
  feature?: FeatureName;
  activePaths?: string[];
  /** Retirada: no se ofrece a nadie. Misma regla que en los ítems principales. */
  retirada?: unknown;
}

interface SidebarProps {
  onDismiss?: () => void;
}

/**
 * Las rutas de TODOS los ítems del menú principal.
 *
 * 🚨 PARA QUÉ. El founder, mirando la barra: «¿por qué en el sidebar se seleccionan ambos?».
 *    «Catálogo» vive en `/dropshipping` y «Billetera» en `/dropshipping/smartydrop/saldo`, así que
 *    el resaltado —que compara por PREFIJO— prendía las dos a la vez. No se notaba antes porque la
 *    Billetera era HIJA: los hijos se resaltan por su cuenta. Al subirla a hermana, su ruta quedó
 *    colgando de la ruta de la otra.
 *
 * ⭐ GANA EL MÁS ESPECÍFICO, que es la regla de cualquier router y la única que no hay que volver a
 *    tocar cuando aparezca un cuarto ítem anidado. Lo contrario —excluir a mano a las hermanas
 *    conocidas— funciona hoy y se rompe en silencio con la próxima.
 */
const RUTAS_DE_LOS_ITEMS: readonly string[] = [
  ...SIDEBAR_MAIN.sections.flatMap((s) => s.items.map((i) => i.url)),
  // Los destinos que la barra resuelve en vivo y no están en la config estática.
  ROUTES.DROPSHIPPING_SMARTYDROP,         // el catálogo, para el rol Drop
  ROUTES.DROPSHIPPING_SMARTYDROP_COBROS,  // la billetera, para la plataforma
  ROUTES.NOVEDADES_GESTION,               // las novedades, para la plataforma
];

/**
 * A dónde lleva «Billetera», que depende de quién la mira.
 *
 * 🚨 DE DÓNDE SALE ESTA FUNCIÓN. Desde el 21-sep hay UNA SOLA CAJA: toda la plata del dropshipper
 *    entra a Smarty, y `fn_drop_cobros_recibidos` no le responde a nadie que no sea la plataforma.
 *    Pero el menú seguía mostrando «Cobros» sólo a un PROVEEDOR — y Smarty HQ no es proveedor. O
 *    sea: el único negocio que puede aprobar un pago era el único sin forma de llegar a la
 *    pantalla. Costó tres pagos sin revisar (S/ 86, desde el 20-sep) hasta que el founder preguntó.
 *
 * ⭐ Y EL ARREGLO NO ES AGREGAR UNA ENTRADA MÁS, es que la que ya existe diga la verdad para cada
 *    uno. «Billetera» es «tu plata» en los dos casos: para el revendedor, lo que le queda para
 *    comprar; para Smarty, lo que recaudó y lo que falta aprobar. Dos entradas distintas peleando
 *    por el mismo nombre habría sido el problema de siempre con otro disfraz.
 */
function aDondeLlevaLaBilletera(esPlataforma: boolean): string {
  return esPlataforma
    ? ROUTES.DROPSHIPPING_SMARTYDROP_COBROS   // lo que ENTRA: cobros por aprobar, aprobados y la caja
    : ROUTES.DROPSHIPPING_SMARTYDROP_SALDO;   // lo que me QUEDA para comprar
}

export function Sidebar({ onDismiss }: SidebarProps) {
  /*
   * La barra lateral empieza a armarse: 34 items filtrados por permisos, con sus submenus.
   *
   * ⭐ SE MIDE PORQUE ERA LA SOSPECHA OBVIA Y RESULTO INOCENTE: la barra tarda 4 ms y todo el
   *    cascaron —proveedores, `Frame`, Topbar— 6 ms. «Aligerar el menu» habria sido trabajo
   *    tirado. La marca queda para que la proxima sospecha tampoco se pruebe a ciegas.
   *
   * 🪤 En el cuerpo y no en un efecto: interesa cuando EMPEZO, no cuando termino.
   */
  marcarHito('barra');
  const location = useLocation();
  const navigate = useNavigate();
  // Sección de Operaciones según el pedido abierto (presupuesto/venta): el detalle vive en una
  // ruta compartida, así que resaltamos Presupuestos/Ventas por el stage, no por la URL.
  const opsActiveSection = useOpsActiveSection((s) => s.section);

  // Estados de expansión de secciones (derivadas de la ruta + forzadas al click)
  const [isOrdersExpanded, setIsOrdersExpanded] = useState(false);
  const [isProductsExpanded, setIsProductsExpanded] = useState(false);
  const [isReportsExpanded, setIsReportsExpanded] = useState(false);
  const [isOnlineStoreExpanded, setIsOnlineStoreExpanded] = useState(false);
  const [isPOSExpanded, setIsPOSExpanded] = useState(false);
  const [isLoyaltyExpanded, setIsLoyaltyExpanded] = useState(false);
  const [isCodExpanded, setIsCodExpanded] = useState(false);
  const [isCrmExpanded, setIsCrmExpanded] = useState(false);
  const [isCallCenterExpanded, setIsCallCenterExpanded] = useState(false);
  const [isWhatsAppExpanded, setIsWhatsAppExpanded] = useState(false);
  const [isOperacionesExpanded, setIsOperacionesExpanded] = useState(false);
  const [isEntregasExpanded, setIsEntregasExpanded] = useState(false);
  const [isTransporteExpanded, setIsTransporteExpanded] = useState(false);
  const [isChatbotExpanded, setIsChatbotExpanded] = useState(false);
  const [isAdsExpanded, setIsAdsExpanded] = useState(false);
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);

  // Estados para badges de notificación
  const [badgeCounts, setBadgeCounts] = useState<Record<string, number>>({
    '/kds': 0,
    [ROUTES.ORDERS]: 0,
    [ROUTES.ORDERS_ONLINE]: 0,
    [ROUTES.ORDERS_ABANDONED]: 0,
  });
  
  const { isOwnerOrManager, loading: authLoading, tenantId, storeSlug } = useAuth();
  const qc = useQueryClient();
  const toUrl = useCallback((url: string) => (storeSlug ? storePath(storeSlug, url) : url), [storeSlug]);
  const { isFeatureEnabled, isDevMode, loading: featuresLoading, planType, isInternal } = useFeatureFlags();
  /*
   * ⭐ EL MENÚ ES DEL ROL. Una cuenta del plan Drop ve la lista blanca de `MENU_DEL_ROL_DROP` y nada
   *    más: los dropshippers dijeron «no sabemos dónde hacer la venta, dónde recargar, dónde
   *    seleccionar la entrega», y lo que veían eran 8 módulos y ~25 destinos. La regla es del PLAN
   *    —misma señal que `RutaSoloParaTiendas`—, no de un módulo apagado.
   *
   * 🪤 En desarrollo NO se aplica, igual que los demás portones: el founder trabaja con una cuenta
   *    de tienda y necesita ver todo.
   */
  const esRolDrop = planType === 'drop'; // el rol es un dato, también en desarrollo (ver FeatureFlagsContext)
  // Dentro del drop, el vendedor ve sus ventas y sus clientes, nada más (ver `rutasDelVendedorDrop`).
  const esVendedorDrop = useSoyVendedorDelDrop();
  /*
   * ⭐ EL MENÚ DEL ALMACÉN ES SU PUERTA. Un proveedor o almacén EXTERNO no paga por ser proveedor:
   *    ve lo que `RUTAS_DEL_ROL_ALMACEN` le deja abrir, y nada más. Antes la puerta cuidaba las
   *    rutas pero el menú no la miraba: veía Chats, Tienda online y Apps, y al tocarlos lo devolvía
   *    a Inicio. Con una sola lista para las dos cosas, lo que se agregue mañana queda afuera solo.
   *    Vale también para una cuenta interna: el rol manda (ver `elPortonDelPlan`).
   */
  const esRolAlmacen = planType === 'almacen';
  // El almacén que es SÓLO almacén (no es proveedor): la lista corta, sin Catálogo ni precios (marca blanca, 23-oct).
  const esSoloAlmacen = useSoloAlmacen();
  const waPerms = useWaPermissions();
  const { role: opsRole } = useOpsRole();

  // Cargar contadores de badges
  useEffect(() => {
    const loadBadgeCounts = async () => {
      if (authLoading || !tenantId) return;
      // No pollear si la pestaña no está visible (evita carga innecesaria).
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

      try {
        /*
         * 🚨 ACÁ HABÍA CUATRO `await` EN FILA. Literal: await, await, await, await — cada uno
         *    esperando al anterior sin necesitarlo. Medido, cada ida y vuelta cuesta ~165 ms, así
         *    que los cuatro numeritos del menú costaban ~660 ms de red PURA, compitiendo con la
         *    carga de la pantalla que el usuario acababa de abrir. Y se repetían cada 30 segundos.
         *
         * ⭐ UN VIAJE. `fn_badges_del_menu` los trae juntos en 9 ms medidos contra producción.
         *
         * 🪤 Sigue contando hasta 100 y parando, como antes: el badge dice «99+» arriba de 99, así
         *    que contar el universo entero es trabajo que se tira. El límite vive adentro de la
         *    función, no acá.
         */
        const { data, error } = await supabase.rpc('fn_badges_del_menu', { p_tenant: tenantId });
        if (error) throw error;
        const b = (data ?? {}) as Record<string, number>;

        setBadgeCounts({
          '/kds': Number(b.kds ?? 0),
          '/orders/online': Number(b.ecommerce ?? 0),
          '/orders/abandoned': Number(b.carritos ?? 0),
          '/orders': Number(b.pedidos ?? 0),
        });
      } catch (error) {
        logError('Error cargando contadores de badges', error);
      }
    };

    loadBadgeCounts();
    const interval = setInterval(loadBadgeCounts, 30000);
    return () => clearInterval(interval);
  }, [authLoading, tenantId]);

  const formatBadge = useCallback((count: number): string | undefined => {
    if (count <= 0) return undefined;
    return count > 99 ? '99+' : count.toString();
  }, []);

  // Helper para verificar si una ruta está activa (usa URL con prefijo store cuando hay storeSlug)
  const isActive = useCallback((url: string): boolean => {
    const currentPath = location.pathname || '';
    const full = toUrl(url);
    return currentPath === full || (full !== '/' && currentPath.startsWith(full));
  }, [location.pathname, toUrl]);

  /**
   * ¿Éste es el ítem que se resalta, o hay otro que calza mejor?
   *
   * La regla vive en `elMasEspecificoGana`, pura y con sus pruebas: acá sólo se le pasan la ruta
   * actual y las de los ítems, ya con el prefijo de la tienda puesto.
   */
  const esElItemQueGana = useCallback(
    (url: string): boolean =>
      ganaElMasEspecifico(location.pathname || '', toUrl(url), RUTAS_DE_LOS_ITEMS.map(toUrl)),
    [location.pathname, toUrl],
  );

  // Helper para crear sub-items con matches y filtrado
  const createSubItems = useCallback((items: SubNavItem[]) => {
    return items
      .filter((item) => {
        // Misma regla que arriba, y por el mismo motivo: retirada se va antes que `isDevMode`.
        if (item.retirada) return false;
        if (item.feature) {
          if (isDevMode) return true;
          return isFeatureEnabled(item.feature);
        }
        if (item.url === '/users' && !isOwnerOrManager()) {
          return false;
        }
        return true;
      })
      .map((item) => {
        const fullUrl = toUrl(item.url);
        /*
         * 🚨 EL SUBMENÚ TENÍA EL MISMO BUG DE PREFIJO QUE LOS PADRES. En Smarty HQ, «Billetera»
         *    lleva a `/dropshipping/smartydrop/cobros`, que empieza con `/dropshipping/smartydrop`
         *    — la ruta del hijo «Smarty Drop». El founder lo vio: los dos resaltados a la vez.
         *
         * 🪤 PERO ACÁ LA REGLA NO ES LA MISMA QUE ARRIBA, y confundirlas rompe el menú: un hijo
         *    compite con sus HERMANOS y con los ítems padre, NUNCA con su propio padre. Si se
         *    compararan todos contra todos, «Catálogo» dejaría de resaltarse justo cuando estás
         *    adentro de él, que es cuando más tiene que estarlo.
         */
        const candidatos = [...RUTAS_DE_LOS_ITEMS, ...items.map((i) => i.url)].map(toUrl);
        const calza = (u: string) => ganaElMasEspecifico(location.pathname || '', toUrl(u), candidatos);
        // 🪤 `activePaths` se deja como estaba: quien los declara está RECLAMANDO esas rutas, y
        //    filtrarlas por especificidad le quitaría justo lo que vino a declarar.
        const baseMatch = item.activePaths ? item.activePaths.some((p) => isActive(p)) : calza(item.url);
        // Dentro del detalle de un pedido, resaltar Presupuestos/Ventas según su etapa (data-driven),
        // ya que la URL del pedido es compartida y no lo distingue.
        const matches =
          item.url === ROUTES.OPS_PEDIDOS ? baseMatch || opsActiveSection === 'pedidos'
            : item.url === ROUTES.OPS_VENTAS ? baseMatch || opsActiveSection === 'ventas'
              : baseMatch;
        return {
          url: fullUrl,
          label: item.label,
          badge: item.badge,
          matches,
          disabled: item.url === '/users' && !isOwnerOrManager(),
          onClick: (event?: MouseEvent) => {
            if (event) {
              event.preventDefault();
              event.stopPropagation();
            }
            startTransition(() => navigate(fullUrl));
          },
        };
      });
  }, [isActive, isFeatureEnabled, isDevMode, isOwnerOrManager, navigate, toUrl, opsActiveSection, location.pathname]);

  // Actualizar estado de expansión cuando cambia la ruta (usa URLs con prefijo store)
  useEffect(() => {
    const currentPath = location.pathname || '';
    setIsOrdersExpanded(
      currentPath.startsWith(toUrl(ROUTES.ORDERS)) ||
      currentPath.startsWith(toUrl(ROUTES.ORDERS_ONLINE)) ||
      currentPath.startsWith(toUrl(ROUTES.ORDERS_ABANDONED))
    );
    setIsProductsExpanded(currentPath.startsWith(toUrl(ROUTES.PRODUCTS_ROOT)));
    setIsReportsExpanded(
      currentPath.startsWith(toUrl(ROUTES.ANALYTICS)) ||
      currentPath.startsWith(toUrl(ROUTES.SALES_HEATMAP)) ||
      currentPath.startsWith(toUrl(ROUTES.SALES_RANKING))
    );
    setIsOnlineStoreExpanded(currentPath.startsWith(toUrl(ROUTES.ONLINE_STORE)));
    setIsPOSExpanded(
      currentPath.startsWith(toUrl(ROUTES.APPS_RESTAURANT)) ||
      currentPath === toUrl('/pos') ||
      currentPath.startsWith(toUrl('/tables'))
    );
    setIsLoyaltyExpanded(currentPath.startsWith(toUrl(ROUTES.APPS_LOYALTY)));
    setIsCodExpanded(currentPath.startsWith(toUrl(ROUTES.APPS_COD)));
    setIsCrmExpanded(currentPath.startsWith(toUrl(ROUTES.APPS_CRM_LEADS)) || currentPath.startsWith(toUrl(ROUTES.APPS_CRM_PIPELINE)) || currentPath.startsWith(toUrl(ROUTES.APPS_CRM_DIALER)) || currentPath.startsWith(toUrl(ROUTES.APPS_CRM_CALLS)));
    setIsOperacionesExpanded(currentPath.startsWith(toUrl(ROUTES.OPS_VENTAS)));
    setIsEntregasExpanded(currentPath.startsWith(toUrl(ROUTES.ENTREGAS)));
    setIsTransporteExpanded(
      currentPath.startsWith(toUrl(ROUTES.TRANSPORT_AGENCIES)) ||
      currentPath.startsWith(toUrl(ROUTES.TRANSPORT_MOTORIZADOS))
    );
    setIsChatbotExpanded(currentPath.startsWith(toUrl(ROUTES.APPS_CHATBOT)));
    setIsAdsExpanded(currentPath.startsWith(toUrl(ROUTES.ADS)));
    setIsSettingsExpanded(currentPath.startsWith(toUrl(ROUTES.SETTINGS)));
  }, [location.pathname, toUrl]);

  // Prefetch de datos del dashboard (Inicio): a 0.5s se precargan; al clicar Inicio suele ser al instante
  useEffect(() => {
    if (authLoading || !tenantId) return;
    const t = setTimeout(() => {
      prefetchDashboardStats(qc, tenantId).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [tenantId, authLoading, qc]);

  // Prefetch de Clientes, Productos e Inventario en segundo plano (1.5s) para que al abrirlos carguen al instante
  useEffect(() => {
    if (authLoading || !tenantId) return;
    const t = setTimeout(() => {
      prefetchCustomersStats(tenantId).catch(() => {});
      prefetchProductsStats(tenantId).catch(() => {});
      prefetchExistenciasStats(tenantId).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [tenantId, authLoading]);

  // Listas de productos (categorías, variantes, etc.): prefetch en idle para acortar la primera visita
  useEffect(() => {
    if (authLoading || !tenantId) return;
    const cancel = scheduleIdleProductListPrefetch(tenantId);
    return cancel;
  }, [tenantId, authLoading]);

  // Clientes, contenido, marketing, descuentos, WhatsApp: chunks lazy sin sección expand dedicada
  useEffect(() => {
    if (authLoading || !tenantId) return;
    const t = setTimeout(() => {
      prefetchLowPriorityAdminChunks();
    }, 2200);
    return () => clearTimeout(t);
  }, [tenantId, authLoading]);

  // Prefetch de chunks y de datos de estadísticas al expandir una sección
  useEffect(() => {
    if (isProductsExpanded) {
      prefetchSection('products');
      if (tenantId) {
        prefetchProductsStats(tenantId).catch(() => {});
        prefetchExistenciasStats(tenantId).catch(() => {});
      }
    }
    if (isReportsExpanded) {
      prefetchSection('reports');
      if (tenantId) prefetchStatisticsStats(tenantId).catch(() => {});
    }
    if (isOnlineStoreExpanded) {
      prefetchSection('onlineStore');
      if (tenantId) {
        prefetchAbandonedCartsStats(tenantId).catch(() => {});
        prefetchShalomAgenciesStats(tenantId).catch(() => {});
        prefetchEcommercePagesStats(tenantId).catch(() => {});
      }
    }
    if (isPOSExpanded) prefetchSection('pos');
    if (isLoyaltyExpanded) {
      prefetchSection('loyalty');
      if (tenantId) prefetchLoyaltyDashboardStats(tenantId).catch(() => {});
    }
    if (isCodExpanded) {
      prefetchSection('cod');
      if (tenantId) {
        prefetchCodAnalyticsStats(tenantId).catch(() => {});
      }
    }
    if (isCrmExpanded) {
      prefetchSection('crm');
      prefetchSection('callCenter');
    }
    if (isCallCenterExpanded) {
      prefetchSection('callCenter');
    }
    if (isEntregasExpanded) {
      prefetchSection('entregas');
    }
    if (isTransporteExpanded) {
      prefetchSection('transporte');
    }
    if (isOrdersExpanded) {
      prefetchSection('orders');
      if (tenantId) {
        prefetchEcommerceOrdersStats(tenantId).catch(() => {});
        prefetchSalesOrdersStats(tenantId).catch(() => {});
        prefetchAbandonedCartsStats(tenantId).catch(() => {});
      }
    }
    if (isOperacionesExpanded) {
      prefetchSection('operaciones');
    }
    if (isChatbotExpanded) prefetchSection('chatbot');
    if (isSettingsExpanded) {
      prefetchSection('settings');
      if (tenantId) {
        prefetchEcommercePaymentMethodsStats(tenantId).catch(() => {});
        prefetchEcommerceDeliveryMethodsStats(tenantId).catch(() => {});
        prefetchEcommerceDeliveryRulesStats(tenantId).catch(() => {});
        prefetchTenantDomainsStats(tenantId).catch(() => {});
        prefetchLogisticsCouriersCoverageStats(tenantId).catch(() => {});
      }
    }
  }, [
    tenantId,
    isProductsExpanded,
    isReportsExpanded,
    isOnlineStoreExpanded,
    isPOSExpanded,
    isLoyaltyExpanded,
    isCodExpanded,
    isCrmExpanded,
    isCallCenterExpanded,
    isOrdersExpanded,
    isEntregasExpanded,
    isTransporteExpanded,
    isOperacionesExpanded,
    isChatbotExpanded,
    isSettingsExpanded,
  ]);

  // Catálogo de sub-items por sección. Mapea SIDEBAR_SUBNAV → SubNavItem[] y
  // aplica badges dinámicos (orders, pos) y filtros de permisos (whatsapp).
  /*
   * ¿Este negocio publica catálogo mayorista? Decide si «Mi almacén» aparece en el menú.
   *
   * 🪤 MISMA CLAVE QUE EL RESTO DEL MÓDULO (`drop-soy-proveedor`), así que la barra lateral no
   *    agrega una consulta: reusa la que ya hace «Mis compras». Y con 5 minutos de frescura, en la
   *    práctica se pregunta una vez por sesión.
   */
  const { datos: soyProveedorDrop = false } = useDatosCacheados<boolean>({
    clave: [CLAVES_DROP.soyProveedor, tenantId],
    activo: !!tenantId,
    frescura: 5 * 60_000,
    traer: async () => !!(await proveedorDelTenant(tenantId!)),
  });
  /* «Inventario» es de quien tiene stock: proveedor o almacén. Los papeles se ganan con un hecho. */
  const { datos: papelesDrop = [] } = useDatosCacheados<string[]>({
    clave: [CLAVES_DROP.misPapeles, tenantId],
    activo: !!tenantId,
    frescura: 5 * 60_000,
    traer: () => misPapeles(tenantId!),
  });
  const tengoStockDrop = papelesDrop.includes('proveedor') || papelesDrop.includes('almacen');
  const administraLaPlata = useAdministraLaPlata();
  /* Una cuenta que es SÓLO courier de Lima: su menú es entregar y cobrar (ver `rutasDelRolCourier`). */
  const esCourierExterno = !isInternal && papelesDrop.includes('courier') && !tengoStockDrop;
  /*
   * «Marketplaces» es de quien integró Mercado Libre o Falabella (founder, 25-sep): sin una cuenta
   * conectada es una pantalla vacía. La conexión se hace en Ajustes → Integraciones. Sólo se
   * pregunta si el plan trae el módulo; los internos lo ven siempre.
   */
  const puedeMarketplaces = isInternal || isFeatureEnabled('marketplaces');
  const { datos: marketplacesIntegrados = [] } = useDatosCacheados<string[]>({
    clave: ['marketplaces-integrados', tenantId],
    activo: !!tenantId && puedeMarketplaces && !isInternal,
    frescura: 5 * 60_000,
    traer: () => misMarketplacesIntegrados(tenantId!),
  });
  const veoMarketplaces = isInternal || (puedeMarketplaces && marketplacesIntegrados.length > 0);

  const subnavByKey = useMemo(() => {
    const toSub = (i: { url: string; label: string; feature?: FeatureName; activePaths?: string[]; retirada?: unknown }): SubNavItem => ({
      url: i.url,
      label: i.label,
      feature: i.feature,
      activePaths: i.activePaths,
      retirada: i.retirada,
    });

    const orders = SIDEBAR_SUBNAV.orders.map((item) => ({
      ...toSub(item),
      badge:
        item.url === ROUTES.ORDERS_ONLINE ? formatBadge(badgeCounts[ROUTES.ORDERS_ONLINE] || 0)
        : item.url === ROUTES.ORDERS_ABANDONED ? formatBadge(badgeCounts[ROUTES.ORDERS_ABANDONED] || 0)
        : undefined,
    }));

    const pos = SIDEBAR_SUBNAV.pos.map((item) => ({
      ...toSub(item),
      badge: item.url === ROUTES.APPS_RESTAURANT_KDS ? formatBadge(badgeCounts['/kds'] || 0) : undefined,
    }));

    const whatsappAll = SIDEBAR_SUBNAV.whatsapp?.map(toSub) ?? [];
    // Agentes solo ven Inbox; supervisores/admins ven todo.
    const whatsapp = (waPerms.isAgent && !waPerms.isSupervisor)
      ? whatsappAll.filter((i) => i.url === ROUTES.WHATSAPP_INBOX)
      : whatsappAll;

    return {
      orders,
      pos,
      whatsapp,
      home: SIDEBAR_SUBNAV.home.map(toSub),
      torre: SIDEBAR_SUBNAV.torre.map(toSub),
      inventarioDrop: SIDEBAR_SUBNAV.inventarioDrop.map(toSub),
      products: SIDEBAR_SUBNAV.products.map(toSub),
      reports: SIDEBAR_SUBNAV.reports.map(toSub),
      onlineStore: SIDEBAR_SUBNAV.onlineStore.map(toSub),
      loyalty: SIDEBAR_SUBNAV.loyalty?.map(toSub) ?? [],
      cod: SIDEBAR_SUBNAV.cod?.map(toSub) ?? [],
      contabilidad: SIDEBAR_SUBNAV.contabilidad?.map(toSub) ?? [],
      // Colas por rol: cada rol ve solo sus colas; lo legacy/extra solo el admin.
      operaciones: (() => {
        const allowed = opsVisibleQueues(opsRole);
        const queueByUrl: Record<string, OpsQueue> = {
          [ROUTES.OPS_PEDIDOS]: 'pedidos', [ROUTES.OPS_VENTAS]: 'ventas',
          [ROUTES.OPS_LOGISTICA]: 'logistica', [ROUTES.OPS_COBRANZAS]: 'cobranzas',
          [ROUTES.OPS_CANCELADAS]: 'canceladas',
        };
        return (SIDEBAR_SUBNAV.operaciones?.map(toSub) ?? []).filter((i) => {
          /*
           * 🪤 «DROP» SÓLO PARA QUIEN ES PROVEEDOR, igual que «Mi almacén». Un tenant que no publica
           *    catálogo mayorista nunca va a recibir un pedido por Smarty Drop: la entrada lo llevaría
           *    siempre a una pantalla vacía.
           */
          // Y también para quien es ALMACÉN: la cola es «lo que despacho», esté el encargo en la cuenta que esté.
          if (i.url === ROUTES.OPS_DROP) return soyProveedorDrop || papelesDrop.includes('almacen');
          const q = queueByUrl[i.url];
          return q ? allowed.includes(q) : opsRole === 'admin';
        });
      })(),
      customers: SIDEBAR_SUBNAV.customers?.map(toSub) ?? [],
      crm: SIDEBAR_SUBNAV.crm?.map(toSub) ?? [],
      marketplaces: SIDEBAR_SUBNAV.marketplaces?.map(toSub) ?? [],
      callCenter: SIDEBAR_SUBNAV.callCenter?.map(toSub) ?? [],
      // Entregas (ADR 0029): sub-items del objeto ENVÍO. No se filtra por cola de pedidos como
      // `operaciones` — acá no hay colas de pedidos, y ese es justamente el punto.
      entregas: (SIDEBAR_SUBNAV.entregas?.map(toSub) ?? []).filter((i) => {
        // ⭐ Las guías de despacho (20261021004300): cada pantalla a quien hace ese trabajo. Guías, el almacén;
        //    Por recoger, el courier; En espera, Smarty.
        if (i.url === ROUTES.ENTREGAS_GUIAS) return papelesDrop.includes('almacen');
        if (i.url === ROUTES.ENTREGAS_POR_RECOGER) return papelesDrop.includes('courier');
        if (i.url === ROUTES.ENTREGAS_EN_ESPERA) return esLaPlataforma(storeSlug);
        return true;
      }),
      transporte: SIDEBAR_SUBNAV.transporte?.map(toSub) ?? [],
      /*
       * 🪤 «MI ALMACÉN» SÓLO PARA QUIEN ES PROVEEDOR. Un tenant que no publica catálogo mayorista no
       *    tiene nada que configurar ahí, y una entrada permanente que lleva a una pantalla vacía es
       *    ruido en el menú de TODAS las tiendas. Mismo criterio que las colas de Operaciones por
       *    rol, unas líneas más arriba.
       */
      /*
       * 🚨 Y «COBROS» TAMBIÉN, que quedó afuera del filtro. Es la bandeja donde se REVISAN las
       *    recargas que a uno le declararon: de la plataforma o de un proveedor, nunca de un
       *    revendedor. El founder lo vio en producción — «cobros debe aparecerle sólo a HQ pero
       *    actualmente también le aparece al dropshipper».
       *
       * 🪤 ACÁ HABÍA UN FILTRO POR PROVEEDOR Y SE FUE CON SUS ÍTEMS (22-sep-2026): «Mi almacén» y
       *    «Cobros» subieron a ítems del grupo «Dropshipping», así que la regla vive donde vive el
       *    ítem. Dejarlo era filtrar una lista que ya no contiene nada que filtrar.
       */
      dropshipping: SIDEBAR_SUBNAV.dropshipping?.map(toSub) ?? [],
      agents: SIDEBAR_SUBNAV.agents?.map(toSub) ?? [],
      settings: SIDEBAR_SUBNAV.settings.map(toSub),
    } as const;
  }, [badgeCounts, formatBadge, waPerms.isAgent, waPerms.isSupervisor, opsRole, soyProveedorDrop, papelesDrop, storeSlug]);

  // Helper para obtener el icono correcto (outline cuando está seleccionado, filled cuando no - como Shopify)
  const getIcon = useCallback((isSelected: boolean, outlineIcon: IconSource, filledIcon: IconSource) => {
    return isSelected ? outlineIcon : filledIcon;
  }, []);

  const getMainIcon = useCallback((key: string) => {
    switch (key) {
      case 'home':
        return getIcon(location.pathname === ROUTES.HOME, HomeIcon, HomeFilledIcon);
      case 'novedades':
        return getIcon(isActive(ROUTES.NOVEDADES), MegaphoneIcon, MegaphoneFilledIcon);
      case 'torre':
        return GaugeIcon;
      case 'equipo':
        return AutomationIcon;
      case 'orders':
        return getIcon(isActive(ROUTES.ORDERS) || isActive(ROUTES.ORDERS_ONLINE) || isActive(ROUTES.ORDERS_ABANDONED), OrderIcon, OrderFilledIcon);
      case 'products':
        return getIcon(isActive(ROUTES.PRODUCTS_ROOT), ProductIcon, ProductFilledIcon);
      case 'customers':
        return getIcon(isActive(ROUTES.CUSTOMERS), CustomersIcon, CustomersFilledIcon);
      case 'analytics':
        return getIcon(isActive(ROUTES.ANALYTICS) || isActive(ROUTES.SALES_HEATMAP) || isActive(ROUTES.SALES_RANKING), ReportIcon, ReportFilledIcon);
      case 'onlineStore':
        return getIcon(isActive(ROUTES.ONLINE_STORE), StoreIcon, StoreFilledIcon);
      case 'pos':
        return PointOfSaleIcon;
      case 'marketplaces':
        return MarketsIcon;
      case 'loyalty':
        return StarIcon;
      case 'cod':
      case 'operaciones':
        return OrderIcon;
      case 'crm':
        return getIcon(isActive(ROUTES.APPS_CRM_LEADS) || isActive(ROUTES.APPS_CRM_PIPELINE), CustomersIcon, CustomersFilledIcon);
      case 'callCenter':
        return PhoneIcon;
      case 'entregas':
      case 'transporte':
        return DeliveryIcon;
      case 'content':
        return ContentIcon;
      case 'marketing':
        return MegaphoneIcon;
      case 'ads':
        return MegaphoneIcon;
      case 'discounts':
        return DiscountIcon;
      case 'modules':
        return AppsIcon;
      case 'whatsapp':
      case 'chatbot':
        return ChatIcon;
      /*
       * 🪤 LOS TRES DE DROPSHIPPING CAÍAN EN EL `default` y salían con el ícono de casa. Se veía en
       *    la barra: «Catálogo» y «Billetera», uno debajo del otro, los dos con una casita.
       */
      case 'dropshipping':
        return CatalogIcon;
      case 'billetera':
        return CashDollarIcon;
      case 'almacenDrop':
        return InventoryIcon;
      case 'inventarioDrop':
        return PackageIcon;
      case 'liquidacionDrop':
        return ReceiptIcon;
      case 'couriersDrop':
        return DeliveryIcon;
      case 'settings':
        return getIcon(isSettingsExpanded, SettingsIcon, SettingsFilledIcon);
      default:
        return undefined;
    }
  }, [getIcon, isActive, isSettingsExpanded, location.pathname]);

  const getMainMatches = useCallback((key: string, url: string) => {
    switch (key) {
      case 'home':
        return location.pathname === ROUTES.HOME || location.pathname === '/';
      case 'orders':
        return isActive(ROUTES.ORDERS) || isActive(ROUTES.ORDERS_ONLINE) || isActive(ROUTES.ORDERS_ABANDONED);
      case 'products':
        return isActive(ROUTES.PRODUCTS_ROOT);
      case 'analytics':
        return isActive(ROUTES.ANALYTICS) || isActive(ROUTES.SALES_HEATMAP) || isActive(ROUTES.SALES_RANKING);
      case 'pos':
        return isActive(ROUTES.APPS_RESTAURANT) || isActive('/apps/restaurante') || isActive('/pos') || isActive('/tables');
      case 'loyalty':
        return isActive(ROUTES.APPS_LOYALTY);
      case 'cod':
        return location.pathname.startsWith(toUrl(ROUTES.APPS_COD));
      case 'crm':
        return location.pathname.startsWith(toUrl(ROUTES.APPS_CRM_LEADS))
          || location.pathname.startsWith(toUrl(ROUTES.APPS_CRM_PIPELINE));
      case 'callCenter':
        return location.pathname.startsWith(toUrl(ROUTES.APPS_CRM_DIALER))
          || location.pathname.startsWith(toUrl(ROUTES.APPS_CRM_CALLS))
          || location.pathname.startsWith(toUrl(ROUTES.APPS_CALL_CENTER));
      case 'whatsapp':
        return location.pathname.startsWith(toUrl(ROUTES.WHATSAPP_INBOX))
          || location.pathname.startsWith(toUrl(ROUTES.WHATSAPP_INBOXES))
          /* Las pantallas nuevas de Chats (conectar, plantillas, comentarios, reglas) cuelgan de /chats. */
          || location.pathname.includes('/chats/')
          || location.pathname.startsWith(toUrl(ROUTES.WHATSAPP_SETTINGS)); // legacy redirect
      case 'operaciones':
        return location.pathname.startsWith(toUrl(ROUTES.OPS_VENTAS));
      case 'transporte':
        return (
          location.pathname.startsWith(toUrl(ROUTES.TRANSPORT_AGENCIES)) ||
          location.pathname.startsWith(toUrl(ROUTES.TRANSPORT_MOTORIZADOS))
        );
      case 'content':
        return location.pathname.startsWith(toUrl(ROUTES.CONTENT));
      case 'marketing':
        return location.pathname.startsWith(toUrl(ROUTES.MARKETING));
      case 'ads':
        return location.pathname.startsWith(toUrl(ROUTES.ADS));
      case 'discounts':
        return location.pathname.startsWith(toUrl(ROUTES.DISCOUNTS));
      case 'modules':
        return location.pathname.startsWith(toUrl(ROUTES.SETTINGS_APPS_AND_SALES_CHANNELS));
      case 'chatbot':
        return location.pathname.startsWith(toUrl(ROUTES.APPS_CHATBOT));
      case 'settings':
        return isSettingsExpanded;
      default:
        /*
         * 🪤 NO ALCANZA CON QUE LA RUTA CALCE: tiene que ser la que MEJOR calza. Los ítems de
         *    arriba resuelven su caso a mano y no pasan por acá; los que sí —entre ellos los tres
         *    de Dropshipping, donde «Billetera» y «Mi almacén» cuelgan de «Catálogo»— se
         *    resaltaban de a dos.
         */
        return esElItemQueGana(url);
    }
  }, [isActive, esElItemQueGana, isSettingsExpanded, location.pathname, toUrl]);

  const getMainBadge = useCallback((key: string, badgeKey?: keyof typeof NAV_BADGES) => {
    if (badgeKey) return NAV_BADGES[badgeKey];
    if (key === 'orders') return formatBadge(badgeCounts[ROUTES.ORDERS] || 0);
    return undefined;
  }, [badgeCounts, formatBadge]);

  // Siempre devolvemos los sub-items a Polaris. Polaris decide cuándo mostrarlos
  // con su lógica interna (selected || expanded || childIsActive). Para los pocos
  // items con toggle puro (onlineStore, loyalty) inyectamos `expanded` en el item
  // builder de abajo. Esto evita el bug histórico de double-gate donde el subnav
  // no aparecía al entrar al módulo por URL directa, Cmd+K o redirect.
  const getMainSubNav = useCallback((subnavKey?: keyof typeof subnavByKey) => {
    if (!subnavKey) return undefined;
    // El rol Drop trae su subnav entero para los módulos que define (ver `MENU_DEL_ROL_DROP`).
    const delRol = esRolDrop ? MENU_DEL_ROL_DROP.subnav[subnavKey] : undefined;
    const todos = delRol ? delRol.map((i) => ({ url: i.url, label: i.label })) : subnavByKey[subnavKey];
    // El almacén externo tampoco ve sub-ítems que su puerta no abre (misma regla que los padres).
    // El almacén ve primero lo que abre «Entregas» para él: sus Guías de despacho (founder, 23-oct).
    const delAlmacen = esRolAlmacen
      ? todos?.filter((i) => laRutaEsDelRolAlmacen(i.url)).sort((a, b) => Number(b.url === ROUTES.ENTREGAS_GUIAS) - Number(a.url === ROUTES.ENTREGAS_GUIAS))
      : todos;
    const delSoloAlmacen = esSoloAlmacen ? delAlmacen?.filter((i) => laRutaEsDelSoloAlmacen(i.url)) : delAlmacen;
    const delCourier = esCourierExterno ? delSoloAlmacen?.filter((i) => laRutaEsDelRolCourier(i.url)) : delSoloAlmacen;
    const items = esVendedorDrop ? delCourier?.filter((i) => laRutaEsDelVendedorDrop(i.url)) : delCourier;
    if (!items) return undefined;
    return createSubItems(items);
  }, [createSubItems, subnavByKey, esRolDrop, esRolAlmacen, esSoloAlmacen, esCourierExterno, esVendedorDrop]);

  const handleMainClick = useCallback((key: string, url: string, event?: MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // startTransition evita flash: React mantiene la pantalla actual hasta que el nuevo contenido esté listo.
    const doNavigate = (targetUrl: string) => {
      startTransition(() => navigate(targetUrl));
    };

    // Mantener el mismo comportamiento actual por item.
    switch (key) {
      case 'products':
        doNavigate(url);
        setIsProductsExpanded(true);
        return;
      case 'analytics':
        doNavigate(url);
        setIsReportsExpanded(true);
        return;
      case 'onlineStore':
        setIsOnlineStoreExpanded(!isOnlineStoreExpanded);
        return;
      case 'pos':
        doNavigate(url);
        setIsPOSExpanded(true);
        return;
      case 'loyalty':
        setIsLoyaltyExpanded(!isLoyaltyExpanded);
        return;
      case 'crm':
        doNavigate(url);
        setIsCrmExpanded(true);
        return;
      case 'callCenter':
        doNavigate(url);
        setIsCallCenterExpanded(true);
        return;
      case 'orders':
        doNavigate(url);
        setIsOrdersExpanded(true);
        return;
      case 'entregas':
        doNavigate(url);
        setIsEntregasExpanded(true);
        return;
      case 'transporte':
        doNavigate(url);
        setIsTransporteExpanded(true);
        return;
      case 'content':
      case 'marketing':
      case 'discounts':
      case 'modules':
        doNavigate(url);
        return;
      case 'whatsapp':
        doNavigate(url);
        setIsWhatsAppExpanded(true);
        return;
      case 'operaciones':
        doNavigate(url);
        setIsOperacionesExpanded(true);
        return;
      case 'chatbot':
        doNavigate(url);
        setIsChatbotExpanded(true);
        return;
      case 'ads':
        doNavigate(url);
        setIsAdsExpanded(true);
        return;
      case 'settings':
        /*
         * 🚨 EL CLIC VA ADONDE APUNTA EL ENLACE (26-oct-2026). Acá estaba fijo `ROUTES.SETTINGS`: el enlace de la
         *    vendedora del drop decía «su Shalom PRO» (`URLS_DEL_VENDEDOR_DROP`), pero el clic la mandaba al hub del
         *    dueño, su portón la devolvía a Ventas y el founder sólo veía «se refresca la página». Cómo: el capturador
         *    de AppLayout (`useSpaInternalNavCapture`) navega primero al enlace; con el router de datos esa navegación
         *    espera un turno, este clic corre después y su destino gana. Para los demás, `url` ya ES `ROUTES.SETTINGS`:
         *    nada cambia. Prueba: `laConfiguracionLlevaAdondeApunta` (montada como producción).
         */
        doNavigate(url);
        setIsSettingsExpanded(true);
        return;
      default:
        doNavigate(url);
        return;
    }
  }, [isOnlineStoreExpanded, isLoyaltyExpanded, isSettingsExpanded, navigate]);

  // Ítems principales con feature solo se muestran si el módulo está habilitado (o en dev)
  const filterSectionItems = useCallback(
    (items: readonly SidebarNavItem[]) =>
      items
        /*
         * ⭐ EL DESTINO SE RESUELVE ANTES DE FILTRAR, no al pintar: así el resaltado de «estoy
         *    acá» y el prefetch miran la MISMA url que el clic. Cuando eso se separa, el menú se
         *    ilumina en un lado y te lleva al otro.
         */
        .map((item) => (item.key === 'billetera'
          ? { ...item, url: aDondeLlevaLaBilletera(esLaPlataforma(storeSlug)) }
          // Smarty HQ publica las novedades; los demás las leen.
          : item.key === 'novedades' && esLaPlataforma(storeSlug)
            ? { ...item, url: ROUTES.NOVEDADES_GESTION }
            // El courier que sólo reparte entra a Entregas por lo suyo: lo que ya le prepararon (20261021004300).
            : item.key === 'entregas' && esCourierExterno
              ? { ...item, url: ROUTES.ENTREGAS_POR_RECOGER }
              : item))
        .filter((item) => {
        /*
         * 🚨 RETIRADA SE VA ANTES QUE `isDevMode`, y ese orden es todo el punto. Una página apagada
         * por `feature` sigue viéndose en dev a propósito —para poder trabajarla—; una retirada NO
         * está construida, así que mostrarla en dev sería ofrecerle al founder justo lo que pidió
         * dejar de ver. Dónde está: Super Admin → «Sin construir».
         */
        if (item.retirada) return false;

        /*
         * 🪤 «Mi almacén» es del que PUBLICA catálogo. Un tenant que no es proveedor no tiene nada
         *    que configurar ahí, y una entrada permanente que lleva a una pantalla vacía es ruido
         *    en el menú de todas las tiendas. Antes esta regla filtraba el subnav; al subir el ítem
         *    a padre se mudó acá con él.
         */
        if (item.key === 'almacenDrop' && !soyProveedorDrop) return false;
        if (item.key === 'inventarioDrop' && !tengoStockDrop) return false;
        // En el plan de socios, la billetera es de quien vende lo suyo: prepaga sus despachos (20260925194000).
        if (item.key === 'billetera' && esRolAlmacen && !soyProveedorDrop) return false;
        // «Tu courier» es del courier: los demás negocios eligen a los de Smarty Drop en Transporte (20260925190000).
        if (item.key === 'couriersDrop' && !papelesDrop.includes('courier')) return false;
        // La torre de control es de Smarty HQ: ve la red entera (la base lo exige igual).
        if (item.key === 'torre' && !esLaPlataforma(storeSlug)) return false;
        // El equipo virtual también es de Smarty HQ (3-nov-2026); la base lo exige igual (fn_equipo_exigir).
        if (item.key === 'equipo' && !esLaPlataforma(storeSlug)) return false;
        // La plata la administran el dueño y el gerente; la base lo exige (20260925202000).
        if ((item.key === 'billetera' || item.key === 'liquidacionDrop') && !administraLaPlata) return false;
        if (item.key === 'marketplaces' && !veoMarketplaces) return false;
        // Las novedades le llegan a los drops y a los actores de Drop; Smarty HQ las publica.
        if (item.key === 'novedades' && !(esRolDrop || esLaPlataforma(storeSlug) || papelesDrop.some((p) => p !== 'revendedor'))) return false;
        // ADR 0029: el rol de Operaciones decide QUÉ MÓDULO ve cada uno, no qué pestaña dentro de
        // un módulo. Quien solo hace logística trabaja en Entregas (objeto envío); Ventas no tiene
        // nada para él y mostrárselo es ruido con permisos que no usa.
        //
        // 🪤 El default de useOpsRole es 'admin' cuando no hay asignación explícita — o sea que
        //    esto no le quita nada a nadie por accidente: solo aplica a quien el founder marcó
        //    a propósito como 'logistica'.
        if (opsRole === 'logistica' && item.key === 'operaciones') return false;

        if (esRolAlmacen && !laRutaEsDelRolAlmacen(item.url)) return false;
        if (esSoloAlmacen && !laRutaEsDelSoloAlmacen(item.url)) return false;
        if (esCourierExterno && !laRutaEsDelRolCourier(item.url)) return false;
        // Lista BLANCA del rol Drop: lo que no está nombrado, no entra. Ver `MENU_DEL_ROL_DROP`.
        if (esRolDrop && !(MENU_DEL_ROL_DROP.items as readonly string[]).includes(item.key)) return false;
        if (esVendedorDrop && !MENU_DEL_VENDEDOR_DROP.includes(item.key)) return false;

        if (item.feature == null) return true;
        if (isDevMode) return true;
        return isFeatureEnabled(item.feature);
      }),
    [isDevMode, isFeatureEnabled, opsRole, esRolDrop, esVendedorDrop, esRolAlmacen, esSoloAlmacen, esCourierExterno, storeSlug, soyProveedorDrop, tengoStockDrop, veoMarketplaces, papelesDrop, administraLaPlata]
  );

  const renderSection = (section: (typeof SIDEBAR_MAIN.sections)[number]) => {
    /*
     * 🪤 UN GRUPO SIN ÍTEMS NO SE DIBUJA. Antes se dibujaba igual: el título quedaba flotando con
     * nada debajo. Nunca se notó porque todos los planes encendían al menos un ítem de cada grupo
     * —otra coincidencia que se rompe con el plan Drop, donde «Canales de venta» y «Apps» quedan
     * completamente vacíos—.
     *
     * Es un no-op para Básico, Pro y Enterprise: ninguno vacía un grupo.
     */
    const filtrados = filterSectionItems(section.items);
    /*
     * El rol Drop además ORDENA como se trabaja: Inicio → Ventas → Catálogo → Clientes. El orden
     * general del menú es por departamentos; para una secuencia lineal el orden es el de la
     * secuencia.
     */
    const orden = MENU_DEL_ROL_DROP.items as readonly string[];
    const visibles = esRolDrop
      ? [...filtrados].sort((a, b) => orden.indexOf(a.key) - orden.indexOf(b.key))
      : filtrados;
    if (visibles.length === 0) return null;

    return (
    <Navigation.Section
      key={section.key}
      title={section.title}
      items={visibles.map((item) => {
        const icon = getMainIcon(item.key);
        // Para el rol Drop, algunos módulos llevan a otra página (Catálogo → Smarty Drop, no el hub con Dropi).
        /*
         * ⭐ UNA SOLA RUTA PARA EL ENLACE Y PARA EL RESALTADO. Antes el enlace usaba la del rol y el
         *    resaltado la de la config: con prefijos se notaba poco, pero es justo la grieta por la
         *    que el menú se ilumina en un lado y te lleva al otro.
         */
        // El vendedor del drop: «Configuración» lo lleva directo a su cuenta de Shalom PRO (23-oct-2026).
        const rutaDelItem = (esVendedorDrop && URLS_DEL_VENDEDOR_DROP[item.key])
          || (esRolDrop && MENU_DEL_ROL_DROP.urls[item.key]) || (esRolAlmacen && MENU_DEL_ROL_ALMACEN_URLS[item.key]) || item.url;
        const fullUrl = toUrl(rutaDelItem);
        const navHoverPrefetch =
          tenantId && !authLoading
            ? () => {
                if (item.key === 'products') prefetchOnMainNavHover('products', tenantId);
                else if (item.key === 'onlineStore') prefetchOnMainNavHover('onlineStore', tenantId);
                else if (item.key === 'settings') prefetchOnMainNavHover('settings', tenantId);
                else if (item.key === 'orders') prefetchOnMainNavHover('orders', tenantId);
                else if (item.key === 'crm') prefetchOnMainNavHover('crm', tenantId);
                // Operaciones es la ruta más lenta del panel según el RUM: precalentar su lista al
                // pasar el mouse es donde más se nota (ver `lib/prefetchNavHover.ts`).
                else if (item.key === 'operaciones') prefetchOnMainNavHover('operaciones', tenantId);
              }
            : undefined;
        // onlineStore y loyalty hacen toggle puro (no navegan al hacer click en
        // el L1). Como no quedan `selected` ni con `childIsActive`, Polaris no
        // los expandiría por sí solo: les inyectamos `expanded` explícito. El
        // resto de items confía en la detección automática de Polaris.
        const expandedOverride =
          item.key === 'onlineStore' ? isOnlineStoreExpanded
          : item.key === 'loyalty' ? isLoyaltyExpanded
          : undefined;
        return {
          url: fullUrl,
          // «Dropshipping» se llama «Catálogo» para el dropshipper: el módulo con el nombre del
          // producto entero era una de las confusiones que nombraron.
          label: (esRolDrop && MENU_DEL_ROL_DROP.etiquetas[item.key]) || item.label,
          icon: icon || HomeIcon,
          matches: getMainMatches(item.key, rutaDelItem),
          badge: getMainBadge(item.key, item.badge),
          onClick: (event?: MouseEvent) => handleMainClick(item.key, fullUrl, event),
          subNavigationItems: getMainSubNav(item.subnavKey),
          ...(expandedOverride !== undefined ? { expanded: expandedOverride } : {}),
          ...(navHoverPrefetch ? { onMouseEnter: navHoverPrefetch } : {}),
        };
      })}
    />
    );
  };

  /*
   * 🚨 MIENTRAS NO SE SEPA QUÉ PUEDE ESTE TENANT, NO SE DIBUJA UN MENÚ.
   *
   * Antes se dibujaba igual, con lo que hubiera en ese momento — y lo que había era una respuesta
   * inventada. Se veía en las dos direcciones:
   *
   *   · cuenta normal  → arrancaba con lo básico y los módulos «aparecían» a los dos segundos
   *   · cuenta Drop    → arrancaba mostrando TODO y a los dos segundos se apagaba solo
   *
   * Las dos parecen un bug del sistema, y la segunda además mostraba los permisos del tenant
   * ANTERIOR dentro del nuevo. La causa era la misma: afirmar antes de saber.
   *
   * Un esqueleto durante ese segundo es honesto: dice «estoy cargando», que es lo que pasa. Un
   * menú que se reacomoda solo dice «algo se rompió».
   */
  if (featuresLoading) {
    return (
      <div className={styles.root}>
        {/* 🪤 También acá: el esqueleto ES el panel durante el primer segundo. Sin cabecera, ese
            segundo se abriría un menú móvil sin forma de cerrarlo. */}
        <CabeceraDelMenuMovil onDismiss={onDismiss} />
        <Box padding="400">
          <SkeletonBodyText lines={9} />
        </Box>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* Cabecera del panel en móvil: cerrar + en qué tienda estoy. Invisible en escritorio. */}
      <CabeceraDelMenuMovil onDismiss={onDismiss} />
      {/* Navegación principal — SCROLLEA cuando el menú es más alto que la pantalla. */}
      <Navigation location={location.pathname || '/'} onDismiss={onDismiss}>
        {SIDEBAR_MAIN.sections.filter((s) => s.key !== 'footer').map(renderSection)}
      </Navigation>
      {/* "Configuración" fija abajo, FUERA del área que scrollea: así nunca se sale de
          cuadro aunque el menú desborde (antes usaba position:sticky y se rompía). */}
      <div className={styles.footer}>
        <Navigation location={location.pathname || '/'} onDismiss={onDismiss}>
          {SIDEBAR_MAIN.sections.filter((s) => s.key === 'footer').map(renderSection)}
        </Navigation>
      </div>
    </div>
  );
}
